import { Head, Link, router } from "@inertiajs/react"
import { AlertTriangle, ChevronDown, ChevronRight, Eye } from "lucide-react"
import { useState } from "react"

import { HpiDataTable, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Item {
  id: number
  type: "mileage" | "expense"
  date: string | null
  case_name: string | null
  case_id: number | null
  employee: string | null
  miles?: number
  rate?: number
  expense_type?: string | null
  description?: string | null
  amount: number
}

interface Employee {
  id: number
  name: string
  mileage_items: Item[]
  mileage_total: number
  expense_items: Item[]
  expense_total: number
}

interface Props {
  employees: Employee[]
  totals: {
    mileage_total: number
    expense_total: number
    count: number
  }
  unapproved_count: number
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function viewHref(row: Item): string | undefined {
  if (!row.case_id) return undefined
  return row.type === "mileage"
    ? `/cases/${row.case_id}/time_entries/${row.id}`
    : `/cases/${row.case_id}/expenses/${row.id}`
}

/* ── Checkbox column helper ────────────────────────────── */

function checkboxColumn<T extends { id: number }>(
  selected: Set<number>,
  toggle: (id: number) => void,
): HpiColumn<T> {
  return {
    header: "",
    width: "max-w-[40px]",
    render: (row) => (
      <input
        type="checkbox"
        checked={selected.has(row.id)}
        onChange={() => toggle(row.id)}
        className="border-hpi-med-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 cursor-pointer rounded"
      />
    ),
  }
}

/* ── View link column ──────────────────────────────────── */

const viewColumn: HpiColumn<Item> = {
  header: "",
  width: "max-w-[40px]",
  render: (row) => {
    const href = viewHref(row)
    if (!href) return null
    return (
      <Link
        href={href}
        className="text-hpi-active-blue hover:text-hpi-blue-dark"
        title="View details"
      >
        <Eye className="h-4 w-4" />
      </Link>
    )
  },
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Pay Expenses", href: "/accounting/expenses" },
]

export default function PayExpenses({
  employees,
  totals,
  unapproved_count,
}: Props) {
  const [selectedMileage, setSelectedMileage] = useState<Set<number>>(new Set())
  const [selectedExpenses, setSelectedExpenses] = useState<Set<number>>(
    new Set(),
  )
  const [reference, setReference] = useState("")
  const [expandedEmployee, setExpandedEmployee] = useState<number | null>(null)

  const toggleMileage = (id: number) => {
    setSelectedMileage((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleExpense = (id: number) => {
    setSelectedExpenses((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const isAllSelectedForEmployee = (emp: Employee): boolean => {
    return (
      emp.mileage_items.every((i) => selectedMileage.has(i.id)) &&
      emp.expense_items.every((i) => selectedExpenses.has(i.id))
    )
  }

  const toggleAllForEmployee = (emp: Employee) => {
    const allSelected = isAllSelectedForEmployee(emp)
    if (allSelected) {
      // Deselect all for this employee
      setSelectedMileage((prev) => {
        const next = new Set(prev)
        emp.mileage_items.forEach((i) => next.delete(i.id))
        return next
      })
      setSelectedExpenses((prev) => {
        const next = new Set(prev)
        emp.expense_items.forEach((i) => next.delete(i.id))
        return next
      })
    } else {
      // Select all for this employee
      setSelectedMileage((prev) => {
        const next = new Set(prev)
        emp.mileage_items.forEach((i) => next.add(i.id))
        return next
      })
      setSelectedExpenses((prev) => {
        const next = new Set(prev)
        emp.expense_items.forEach((i) => next.add(i.id))
        return next
      })
    }
  }

  const totalSelected = selectedMileage.size + selectedExpenses.size

  const handleMarkPaid = () => {
    if (totalSelected === 0) return alert("Please select items to mark as paid")

    router.post("/accounting/expenses/mark_paid", {
      reference: reference.trim(),
      mileage_ids: Array.from(selectedMileage),
      expense_ids: Array.from(selectedExpenses),
    })
  }

  // Build columns with checkbox + view columns
  const mileageCols: HpiColumn<Item>[] = [
    checkboxColumn(selectedMileage, toggleMileage),
    {
      header: "Date",
      width: "max-w-[120px]",
      render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
    },
    {
      header: "Case",
      render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
    },
    {
      header: "Miles",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">{row.miles ?? 0}</div>
      ),
    },
    {
      header: "Rate",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatCurrency(row.rate ?? 0)}
        </div>
      ),
    },
    {
      header: "Amount",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-right text-sm font-medium">
          {formatCurrency(row.amount)}
        </div>
      ),
    },
    viewColumn,
  ]

  const expenseCols: HpiColumn<Item>[] = [
    checkboxColumn(selectedExpenses, toggleExpense),
    {
      header: "Date",
      width: "max-w-[120px]",
      render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
    },
    {
      header: "Case",
      render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
    },
    {
      header: "Type",
      width: "max-w-[120px]",
      render: (row) => <div className="text-sm">{row.expense_type ?? "-"}</div>,
    },
    {
      header: "Description",
      render: (row) => <div className="text-sm">{row.description ?? "-"}</div>,
    },
    {
      header: "Amount",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-right text-sm font-medium">
          {formatCurrency(row.amount)}
        </div>
      ),
    },
    viewColumn,
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Pay Expenses" />

      <div className="p-6">
        <HpiStatsBar
          stats={[
            {
              label: "Unpaid",
              subLabel: "Mileage",
              value: formatCurrency(totals.mileage_total),
            },
            {
              label: "Unpaid",
              subLabel: "Expenses",
              value: formatCurrency(totals.expense_total),
            },
            { label: "Total", subLabel: "Items", value: totals.count },
          ]}
          className="mb-6"
        />

        {/* Unapproved entries warning banner */}
        {unapproved_count > 0 && (
          <Link
            href="/reports/case_activity"
            className="mb-6 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 transition-colors hover:bg-amber-100"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
            <span className="text-sm font-medium">
              {unapproved_count} Time &amp; Expense{" "}
              {unapproved_count === 1 ? "Entry is" : "Entries are"} Unapproved
            </span>
          </Link>
        )}

        {/* Mark Paid bar — always visible when there are employees */}
        {employees.length > 0 && (
          <div className="border-hpi-light-gray mb-6 flex items-center gap-4 rounded-lg border bg-white p-4">
            <label className="text-hpi-dark-gray text-sm font-medium">
              Ref #:
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={50}
              className="border-hpi-light-gray focus:border-hpi-active-blue w-48 rounded border px-3 py-2 text-sm focus:outline-none"
              placeholder="Payment reference (optional)"
            />
            <button
              onClick={handleMarkPaid}
              disabled={totalSelected === 0}
              className="bg-hpi-dark-green rounded px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Mark Paid ({totalSelected} items)
            </button>
          </div>
        )}

        {/* Employee sections */}
        {employees.map((emp) => {
          const allSelected = isAllSelectedForEmployee(emp)
          return (
            <div
              key={emp.id}
              className="border-hpi-light-gray mb-4 rounded-lg border bg-white"
            >
              <button
                className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
                onClick={() =>
                  setExpandedEmployee(
                    expandedEmployee === emp.id ? null : emp.id,
                  )
                }
              >
                <div className="flex items-center">
                  {expandedEmployee === emp.id ? (
                    <ChevronDown className="text-hpi-dark-gray mr-2 h-4 w-4" />
                  ) : (
                    <ChevronRight className="text-hpi-dark-gray mr-2 h-4 w-4" />
                  )}
                  <span className="text-hpi-dark-gray font-semibold">
                    {emp.name}
                  </span>
                  <span className="text-hpi-med-blue ml-4 text-sm">
                    {emp.mileage_items.length + emp.expense_items.length} items
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">
                    {formatCurrency(emp.mileage_total + emp.expense_total)}
                  </span>
                  <button
                    className={`rounded px-3 py-1 text-xs text-white ${allSelected ? "bg-hpi-med-gray hover:bg-gray-500" : "bg-hpi-active-blue hover:bg-hpi-blue-dark"}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleAllForEmployee(emp)
                    }}
                  >
                    {allSelected ? "Deselect All" : "Select All"}
                  </button>
                </div>
              </button>

              {expandedEmployee === emp.id && (
                <div className="border-hpi-light-gray border-t p-4">
                  {emp.mileage_items.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
                        Unpaid Mileage
                      </h4>
                      <HpiDataTable<Item>
                        title="Mileage"
                        columns={mileageCols}
                        data={emp.mileage_items}
                        keyFn={(row) => row.id}
                      />
                      <div className="mt-1 text-right text-sm font-medium">
                        Total: {formatCurrency(emp.mileage_total)}
                      </div>
                    </div>
                  )}

                  {emp.expense_items.length > 0 && (
                    <div>
                      <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
                        Unpaid Cash Expenses
                      </h4>
                      <HpiDataTable<Item>
                        title="Expenses"
                        columns={expenseCols}
                        data={emp.expense_items}
                        keyFn={(row) => row.id}
                      />
                      <div className="mt-1 text-right text-sm font-medium">
                        Total: {formatCurrency(emp.expense_total)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {employees.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No unpaid expenses found.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
